import { api } from './client';

// Rails models and strong params own `email_address`, while the admin users
// form keeps `email` as its internal field name. Map the field at this API
// boundary in both directions — otherwise an admin email edit is silently
// dropped by `user_params` (strong params never see the key) and backend
// responses render without an email in the users list/modal.
const toApiUserPayload = (data: Record<string, unknown>): Record<string, unknown> => {
  if (data == null || typeof data !== 'object' || !('email' in data)) {
    return data;
  }
  const { email, ...rest } = data;
  return { ...rest, email_address: email };
};

const fromApiUser = (user: Record<string, unknown> | undefined | null): Record<string, unknown> | undefined | null => {
  if (user == null || typeof user !== 'object' || !('email_address' in user)) {
    return user;
  }
  const { email_address, ...rest } = user;
  return { ...rest, email: email_address };
};

export const usersApi = {
  async getAdminUsers(filters: Record<string, string> = {}): Promise<{ users: any[] }> {
    const params = new URLSearchParams(filters);
    const response = await api.request<{ users: any[] }>(`/admin/users?${params}`);
    // Preserve any other top-level envelope fields the endpoint may return.
    return { ...response, users: (response?.users ?? []).map((user) => fromApiUser(user)) };
  },

  async getAdminUser(id: number | string): Promise<{ user: any }> {
    const response = await api.request<{ user: any }>(`/admin/users/${id}`);
    return { ...response, user: fromApiUser(response?.user) };
  },

  async createAdminUser(data: any): Promise<{ user: any }> {
    const response = await api.request<{ user: any }>('/admin/users', {
      method: 'POST',
      body: { user: toApiUserPayload(data) } as any
    });
    return { ...response, user: fromApiUser(response?.user) };
  },

  async updateAdminUser(id: number | string, data: any): Promise<{ user: any }> {
    const response = await api.request<{ user: any }>(`/admin/users/${id}`, {
      method: 'PATCH',
      body: { user: toApiUserPayload(data) } as any
    });
    return { ...response, user: fromApiUser(response?.user) };
  },

  deleteAdminUser(id: number | string): Promise<{ message: string }> {
    return api.request<{ message: string }>(`/admin/users/${id}`, {
      method: 'DELETE'
    });
  },

  async deactivateAdminUser(id: number | string): Promise<{ user: any }> {
    const response = await api.request<{ user: any }>(`/admin/users/${id}/deactivate`, {
      method: 'POST'
    });
    return { ...response, user: fromApiUser(response?.user) };
  },

  async reactivateAdminUser(id: number | string): Promise<{ user: any }> {
    const response = await api.request<{ user: any }>(`/admin/users/${id}/reactivate`, {
      method: 'POST'
    });
    return { ...response, user: fromApiUser(response?.user) };
  },
};
